/**
 * Employee Configuration — one page for Employee Types + Employee Numbering.
 *
 * Types persist via PATCH /organisation/me/hr-settings under payroll.employment_types.
 * Numbering persists in hr_settings.employee_numbering (flat legacy fields kept in sync).
 * Next employee number is loaded from GET /employees/next-code (backend source of truth).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  CheckCircle2,
  Circle,
  Hash,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";

import "../../styles/employeeSetup.css";
import API from "../../services/api";
import { getEmployees, getNextEmployeeCode } from "../../Moduels/Employees/EmployeeApi";
import { DEFAULT_EMPLOYEE_TYPE } from "../../Moduels/Employees/employeeTypeConstants";
import BrixigoPageLoader from "../common/BrixigoPageLoader";
import ConfirmDialog from "../ui/ConfirmDialog";
import { ToastStack, useToastStack } from "../ui/ToastStack";
import {
  EMP_CONFIG_PATH,
  DEFAULT_NUMBERING,
  buildPreviewCodes,
  countEmployeesByType,
  emptyEtForm,
  filterEmployeeTypes,
  formatNumberingExample,
  getConfigCompletion,
  hashToSection,
  isNumberingConfigured,
  isNumberingDirty,
  isTypesConfigured,
  lookupTypeCount,
  normalizeList,
  numberingPersistedFromHr,
  parseEmployeeListPayload,
  seedFromConstants,
  validateEtForm,
} from "./employeeConfigurationUtils";
import { navigateToSettingsHome } from "./settingsExit";

function formatApiError(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((i) => (typeof i === "string" ? i : i?.msg || JSON.stringify(i)))
      .join("; ");
  if (typeof detail === "object") return detail.message || detail.msg || JSON.stringify(detail);
  return fallback;
}

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  if (typeof el.focus === "function") {
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }
}

export default function EmployeeSetupPage() {
  const { toasts, addToast, removeToast } = useToastStack();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [types, setTypes] = useState([]);
  const [etSaving, setEtSaving] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEtForm());
  const [formErrors, setFormErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [typeSearch, setTypeSearch] = useState("");
  const [typeStatus, setTypeStatus] = useState("all");
  const [typeCounts, setTypeCounts] = useState({});

  const [numbering, setNumbering] = useState(DEFAULT_NUMBERING);
  const [savedNumbering, setSavedNumbering] = useState(DEFAULT_NUMBERING);
  const [numberingPersisted, setNumberingPersisted] = useState(false);
  const [numSaving, setNumSaving] = useState(false);
  const [prefixError, setPrefixError] = useState("");
  const [nextCode, setNextCode] = useState(null);
  const [nextCodeLoading, setNextCodeLoading] = useState(false);
  const [leaveGuard, setLeaveGuard] = useState(null);

  const numDirty = isNumberingDirty(numbering, savedNumbering);
  const numDirtyRef = useRef(numDirty);
  numDirtyRef.current = numDirty;

  const persistTypes = useCallback(async (nextTypes) => {
    const res = await API.patch("/organisation/me/hr-settings", {
      payroll: { employment_types: nextTypes },
    });
    const payroll = res.data?.payroll_settings || res.data?.hr_settings?.payroll || {};
    const saved = normalizeList(payroll["employment_types"] ?? nextTypes);
    setTypes(saved);
    return saved;
  }, []);

  const loadNextCode = useCallback(async () => {
    setNextCodeLoading(true);
    try {
      const data = await getNextEmployeeCode();
      setNextCode(data || null);
    } catch {
      setNextCode(null);
    } finally {
      setNextCodeLoading(false);
    }
  }, []);

  const loadTypeCounts = useCallback(async () => {
    try {
      const collected = [];
      let page = 1;
      let total = Infinity;
      while (collected.length < total && page <= 10) {
        const data = await getEmployees({ page, page_size: 200, active_only: false });
        const parsed = parseEmployeeListPayload(data);
        collected.push(...parsed.items);
        total = parsed.total;
        if (!parsed.items.length || parsed.items.length < 200) break;
        page += 1;
      }
      setTypeCounts(countEmployeesByType(collected));
    } catch {
      setTypeCounts({});
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get("/organisation/me/hr-settings");
      const hr = res.data?.hr_settings || {};
      const payroll = res.data?.payroll_settings || hr.payroll || {};

      let list = normalizeList(payroll["employment_types"]);
      if (!list.length) {
        const seeded = seedFromConstants();
        await persistTypes(seeded);
        setTypes(seeded);
      } else {
        setTypes(list);
      }

      const stored = hr.employee_numbering;
      const prefix = hr.employee_prefix;
      const auto =
        stored?.auto_generate ?? hr.employee_auto_increment ?? DEFAULT_NUMBERING.auto_generate;
      const usePrefix =
        stored?.use_prefix ?? hr.employee_use_prefix ?? DEFAULT_NUMBERING.use_prefix;
      const nextNumbering = {
        ...DEFAULT_NUMBERING,
        ...(stored || {}),
        auto_generate: Boolean(auto),
        use_prefix: usePrefix !== false,
        prefix: stored?.prefix || prefix || DEFAULT_NUMBERING.prefix,
        starting_number:
          stored?.starting_number ||
          stored?.start_number ||
          hr.employee_number_start ||
          DEFAULT_NUMBERING.starting_number,
      };
      setNumbering(nextNumbering);
      setSavedNumbering(nextNumbering);
      setNumberingPersisted(numberingPersistedFromHr(hr));
      await Promise.all([loadNextCode(), loadTypeCounts()]);
    } catch (err) {
      addToast(formatApiError(err, "Failed to load employee configuration"), "error");
    } finally {
      setLoading(false);
    }
  }, [addToast, loadNextCode, loadTypeCounts, persistTypes]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (loading) return;
    const section = hashToSection(location.hash);
    if (!section) return;
    const timer = window.setTimeout(() => scrollToSection(section), 50);
    return () => window.clearTimeout(timer);
  }, [loading, location.hash]);

  useEffect(() => {
    const onLeave = (event) => {
      if (!numDirtyRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, []);

  const jumpTo = (sectionId) => {
    navigate(`${EMP_CONFIG_PATH}#${sectionId}`, { replace: true });
    scrollToSection(sectionId);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyEtForm());
    setFormErrors({});
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name || "", description: row.description || "" });
    setFormErrors({});
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (etSaving) return;
    setDrawerOpen(false);
    setEditing(null);
    setForm(emptyEtForm());
    setFormErrors({});
  };

  const handleEtSave = async (e) => {
    e?.preventDefault?.();
    const errors = validateEtForm(form, types, editing?.id);
    setFormErrors(errors);
    if (Object.keys(errors).length) return;
    setEtSaving(true);
    try {
      let next;
      if (editing?.id) {
        next = types.map((row) =>
          row.id === editing.id
            ? { ...row, name: form.name.trim(), description: form.description.trim() }
            : row
        );
      } else {
        next = [
          ...types,
          {
            id: `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
            name: form.name.trim(),
            description: form.description.trim(),
            is_system: false,
            is_default: false,
            is_active: true,
          },
        ];
      }
      await persistTypes(next);
      addToast(editing ? "Employee type updated" : "Employee type added", "success");
      closeDrawer();
      navigateToSettingsHome(navigate, { replace: true });
    } catch (err) {
      addToast(formatApiError(err, "Failed to save employee type"), "error");
    } finally {
      setEtSaving(false);
    }
  };

  const toggleActive = async (row) => {
    const next = types.map((t) => (t.id === row.id ? { ...t, is_active: !t.is_active } : t));
    try {
      await persistTypes(next);
      addToast(
        `${row.name} marked ${row.is_active ? "inactive" : "active"}`,
        "success"
      );
    } catch (err) {
      addToast(formatApiError(err, "Failed to update status"), "error");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.is_default || deleteTarget.name === DEFAULT_EMPLOYEE_TYPE) {
      addToast("The default employee type cannot be deleted", "error");
      setDeleteTarget(null);
      return;
    }
    const assigned = lookupTypeCount(typeCounts, deleteTarget.name);
    if (assigned > 0) {
      const next = types.map((t) =>
        t.id === deleteTarget.id ? { ...t, is_active: false } : t
      );
      setEtSaving(true);
      try {
        await persistTypes(next);
        addToast(
          `"${deleteTarget.name}" is assigned to ${assigned} employee${assigned === 1 ? "" : "s"} so it was deactivated instead of deleted.`,
          "success"
        );
        setDeleteTarget(null);
      } catch (err) {
        addToast(formatApiError(err, "Failed to deactivate employee type"), "error");
      } finally {
        setEtSaving(false);
      }
      return;
    }
    setEtSaving(true);
    try {
      const next = types.filter((row) => row.id !== deleteTarget.id);
      await persistTypes(next);
      addToast(`"${deleteTarget.name}" deleted`, "success");
      setDeleteTarget(null);
    } catch (err) {
      addToast(formatApiError(err, "Failed to delete employee type"), "error");
    } finally {
      setEtSaving(false);
    }
  };

  const saveNumbering = async ({ silent = false } = {}) => {
    const prefixOn = numbering.use_prefix !== false;
    const letters = (numbering.prefix || "")
      .replace(/[^A-Za-z]/g, "")
      .toUpperCase()
      .slice(0, 4);
    if (prefixOn && !letters) {
      setPrefixError("Enter a prefix, or turn Prefix off to use numbers only.");
      jumpTo("employee-numbering");
      return false;
    }
    setPrefixError("");
    setNumSaving(true);
    try {
      const payload = {
        ...numbering,
        use_prefix: prefixOn,
        prefix: prefixOn ? letters : numbering.prefix || "",
      };
      await API.patch("/organisation/me/hr-settings", {
        employee_prefix: prefixOn ? letters : "",
        employee_auto_increment: Boolean(numbering.auto_generate),
        employee_number_start: numbering.starting_number,
        employee_use_prefix: prefixOn,
        employee_numbering: payload,
      });
      setNumbering(payload);
      setSavedNumbering(payload);
      setNumberingPersisted(true);
      await loadNextCode();
      if (!silent) {
        addToast("Employee numbering updated successfully", "success");
        navigateToSettingsHome(navigate, { replace: true });
      }
      return true;
    } catch (err) {
      addToast(formatApiError(err, "Failed to save numbering"), "error");
      return false;
    } finally {
      setNumSaving(false);
    }
  };

  const handleNumSave = async () => {
    await saveNumbering();
  };

  const confirmLeave = async () => {
    const action = leaveGuard;
    setLeaveGuard(null);
    if (!action) return;
    if (action.mode === "save") {
      const ok = await saveNumbering({ silent: true });
      if (!ok) return;
    }
    navigate(action.to);
  };

  const previewCodes = useMemo(
    () =>
      buildPreviewCodes(
        numbering.prefix,
        numbering.suffix,
        numbering.starting_number,
        numbering.number_length,
        4,
        numbering.use_prefix !== false
      ),
    [numbering]
  );

  const filteredTypes = useMemo(
    () => filterEmployeeTypes(types, { search: typeSearch, status: typeStatus }),
    [types, typeSearch, typeStatus]
  );

  const typesConfigured = isTypesConfigured(types);
  const numberingConfigured = isNumberingConfigured(savedNumbering, numberingPersisted);
  const completion = getConfigCompletion({ typesConfigured, numberingConfigured });
  const savedFormat = formatNumberingExample(savedNumbering);
  const assignedToDelete = deleteTarget
    ? lookupTypeCount(typeCounts, deleteTarget.name)
    : 0;

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[300px]">
        <BrixigoPageLoader label="Loading employee configuration…" />
      </div>
    );
  }

  return (
    <div className="es-page">
      <ToastStack toasts={toasts} onRemove={removeToast} />

      <header className="es-page-header">
        <div className="es-header-row">
          <div>
            <h1 className="es-page-title">Employee Configuration</h1>
          </div>
          <span
            className={`es-header-status ${completion.complete ? "es-header-status--done" : "es-header-status--pending"}`}
          >
            {completion.complete ? (
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <Circle className="h-3.5 w-3.5" aria-hidden />
            )}
            {completion.complete ? "Complete" : completion.title}
          </span>
        </div>
      </header>

      <div className="es-overview-grid">
        <button
          type="button"
          className="es-overview-card"
          onClick={() => jumpTo("employee-types")}
          aria-label="Manage employee types"
        >
          <div className="es-overview-card-top">
            <div className="es-panel-icon-wrap" aria-hidden>
              <Users className="h-5 w-5" />
            </div>
            <span
              className={`es-config-badge ${typesConfigured ? "es-config-badge--done" : "es-config-badge--pending"}`}
            >
              {typesConfigured ? "Configured" : "Needs Setup"}
            </span>
          </div>
          <h2 className="es-overview-title">Employee Types</h2>
          <p className="es-overview-desc">Define the employment types used across your organization.</p>
          <p className="es-overview-metric">
            {types.length} {types.length === 1 ? "Type" : "Types"}
          </p>
          <span className="es-overview-action">
            Manage Types
            <ArrowRight className="h-4 w-4" aria-hidden />
          </span>
        </button>

        <button
          type="button"
          className="es-overview-card"
          onClick={() => jumpTo("employee-numbering")}
          aria-label="Configure employee numbering"
        >
          <div className="es-overview-card-top">
            <div className="es-panel-icon-wrap es-panel-icon-wrap--hash" aria-hidden>
              <Hash className="h-5 w-5" />
            </div>
            <span
              className={`es-config-badge ${numberingConfigured ? "es-config-badge--done" : "es-config-badge--pending"}`}
            >
              {numberingConfigured ? "Configured" : "Needs Setup"}
            </span>
          </div>
          <h2 className="es-overview-title">Employee Numbering</h2>
          <p className="es-overview-desc">Configure how employee numbers are automatically generated.</p>
          {numberingConfigured ? (
            <div className="es-overview-metrics">
              <span>Format: {savedFormat}</span>
              <span>Next Number: {nextCode?.employee_code || (nextCodeLoading ? "…" : "—")}</span>
            </div>
          ) : (
            <p className="es-overview-metric">Not configured</p>
          )}
          <span className="es-overview-action">
            Configure Numbering
            <ArrowRight className="h-4 w-4" aria-hidden />
          </span>
        </button>
      </div>

      <section
        className="es-panel"
        id="employee-types"
        tabIndex={-1}
        aria-labelledby="employee-types-heading"
      >
        <div className="es-panel-header">
          <div className="es-panel-icon-wrap" aria-hidden>
            <Briefcase className="h-5 w-5" />
          </div>
          <div className="es-panel-title-group">
            <h2 id="employee-types-heading" className="es-panel-title">
              Employee Types
            </h2>
            <p className="es-panel-sub">
              Define and manage the types of employment used in your organization.
            </p>
          </div>
          <button type="button" className="es-add-btn" onClick={openCreate}>
            <Plus className="h-4 w-4" aria-hidden />
            Add Employee Type
          </button>
        </div>

        {types.length === 0 ? (
          <div className="es-empty-state">
            <Users className="h-8 w-8" aria-hidden />
            <h3>No Employee Types Yet</h3>
            <p>Create your first employee type to classify employees within your organization.</p>
            <button type="button" className="es-add-btn" onClick={openCreate}>
              <Plus className="h-4 w-4" aria-hidden />
              Add Employee Type
            </button>
          </div>
        ) : (
          <>
            <div className="es-toolbar">
              <label className="es-search-wrap">
                <Search className="h-4 w-4" aria-hidden />
                <input
                  type="search"
                  className="es-search-input"
                  placeholder="Search employee types..."
                  value={typeSearch}
                  onChange={(e) => setTypeSearch(e.target.value)}
                  aria-label="Search employee types by name or description"
                />
              </label>
              <label className="es-filter-wrap">
                <span className="sr-only">Filter by status</span>
                <select
                  className="es-filter-select"
                  value={typeStatus}
                  onChange={(e) => setTypeStatus(e.target.value)}
                  aria-label="Filter employee types by status"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>
            </div>

            <div className="es-table-wrap">
              <table className="es-table">
                <thead>
                  <tr>
                    <th>Type Name</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Employees</th>
                    <th className="es-th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTypes.map((row) => (
                    <tr key={row.id}>
                      <td className="es-td-name">
                        {row.name}
                        {(row.is_default || row.name === DEFAULT_EMPLOYEE_TYPE) && (
                          <span className="es-badge es-badge--default">Default</span>
                        )}
                      </td>
                      <td className="es-td-desc">
                        {row.description || <span className="es-empty">—</span>}
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`es-status-badge ${row.is_active ? "es-status-badge--active" : "es-status-badge--inactive"}`}
                          onClick={() => toggleActive(row)}
                          title={row.is_active ? "Deactivate this type" : "Activate this type"}
                          aria-label={`${row.name} is ${row.is_active ? "Active" : "Inactive"}. Click to ${row.is_active ? "deactivate" : "activate"}.`}
                        >
                          {row.is_active ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td>{lookupTypeCount(typeCounts, row.name)}</td>
                      <td className="es-td-actions">
                        <button
                          type="button"
                          className="es-icon-btn es-icon-btn--edit"
                          onClick={() => openEdit(row)}
                          title="Edit"
                          aria-label={`Edit ${row.name}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {!row.is_default && row.name !== DEFAULT_EMPLOYEE_TYPE && (
                          <button
                            type="button"
                            className="es-icon-btn es-icon-btn--delete"
                            onClick={() => setDeleteTarget(row)}
                            title={
                              lookupTypeCount(typeCounts, row.name) > 0
                                ? "Deactivate"
                                : "Delete"
                            }
                            aria-label={
                              lookupTypeCount(typeCounts, row.name) > 0
                                ? `Deactivate ${row.name}`
                                : `Delete ${row.name}`
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredTypes.length === 0 && (
                    <tr>
                      <td colSpan={5} className="es-empty-row">
                        No employee types match this search or filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="es-panel-footer-info">
          <span className="es-info-icon">ℹ</span>
          These employee types will be available while creating or managing employee records.
        </div>
      </section>

      <section
        className="es-panel"
        id="employee-numbering"
        tabIndex={-1}
        aria-labelledby="employee-numbering-heading"
      >
        <div className="es-panel-header es-panel-header--simple">
          <div className="es-panel-icon-wrap es-panel-icon-wrap--hash" aria-hidden>
            <Hash className="h-5 w-5" />
          </div>
          <div className="es-panel-title-group">
            <h2 id="employee-numbering-heading" className="es-panel-title">
              Employee Numbering
            </h2>
            <p className="es-panel-sub">
              Configure how employee numbers are generated and assigned automatically.
            </p>
          </div>
        </div>

        {!numberingConfigured && (
          <div className="es-empty-banner">
            <div>
              <h3>Employee Numbering Not Configured</h3>
              <p>Configure employee numbering so new employees can receive IDs automatically.</p>
            </div>
          </div>
        )}

        {numbering.auto_generate ? (
          <div className="es-num-success-banner">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" aria-hidden />
            <span>
              Auto generate is enabled. New employees get the next code
              {numbering.use_prefix === false
                ? " as numbers only."
                : " with this organisation's prefix."}{" "}
              Existing employee numbers are not changed.
            </span>
          </div>
        ) : (
          <div className="es-num-manual-banner">
            <span>
              Auto numbering is off for this organisation. Users enter the employee code
              manually (e.g. AAA1001, 2001, or 10001).
            </span>
          </div>
        )}

        <div className="es-num-body">
          <div className="es-num-row es-num-row--toggle">
            <div>
              <span className="es-num-label" id="auto-generate-label">
                Auto Generate
              </span>
              <p className="es-num-hint" style={{ marginTop: 4 }}>
                Turn off to let HR type each code (useful when existing staff already use 1001, 2001, …).
              </p>
            </div>
            <label className="es-toggle-wrap">
              <input
                type="checkbox"
                className="sr-only"
                checked={Boolean(numbering.auto_generate)}
                onChange={(e) =>
                  setNumbering((n) => ({ ...n, auto_generate: e.target.checked }))
                }
                aria-labelledby="auto-generate-label"
              />
              <span className={`es-toggle ${numbering.auto_generate ? "es-toggle--on" : ""}`} />
              <span className="es-toggle-label-txt">
                {numbering.auto_generate ? "Yes" : "No"}
              </span>
            </label>
          </div>

          <div className="es-num-row es-num-row--toggle">
            <div>
              <span className="es-num-label" id="prefix-toggle-label">
                Prefix
                {numbering.use_prefix !== false && <span className="es-required"> *</span>}
              </span>
              <p className="es-num-hint" style={{ marginTop: 4 }}>
                {numbering.use_prefix !== false
                  ? "Add letters before the number (e.g. ABC0001)."
                  : "Off — employee codes will be numbers only (e.g. 0001, 0002)."}
              </p>
            </div>
            <label className="es-toggle-wrap">
              <input
                type="checkbox"
                className="sr-only"
                checked={numbering.use_prefix !== false}
                onChange={(e) => {
                  const on = e.target.checked;
                  setPrefixError("");
                  setNumbering((n) => ({ ...n, use_prefix: on }));
                }}
                aria-labelledby="prefix-toggle-label"
              />
              <span className={`es-toggle ${numbering.use_prefix !== false ? "es-toggle--on" : ""}`} />
              <span className="es-toggle-label-txt">
                {numbering.use_prefix !== false ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {numbering.use_prefix !== false && (
            <div className="es-num-field">
              <label className="es-num-field-label" htmlFor="es-prefix">
                Letters
              </label>
              <input
                id="es-prefix"
                type="text"
                className={`es-num-input${prefixError ? " es-num-input--err" : ""}`}
                value={numbering.prefix}
                maxLength={4}
                placeholder="EMP"
                autoComplete="off"
                onChange={(e) => {
                  setPrefixError("");
                  setNumbering((n) => ({
                    ...n,
                    prefix: e.target.value.toUpperCase().replace(/[^A-Z]/g, ""),
                  }));
                }}
              />
              {prefixError ? (
                <p className="es-num-error">{prefixError}</p>
              ) : (
                <p className="es-num-hint">
                  Prefix will be added before the number. Example: {previewCodes[0] || "EMP0001"}
                </p>
              )}
            </div>
          )}

          <div className="es-num-field">
            <label className="es-num-field-label" htmlFor="es-suffix">
              Suffix
            </label>
            <input
              id="es-suffix"
              type="text"
              className="es-num-input"
              value={numbering.suffix}
              maxLength={4}
              placeholder="Enter suffix (optional)"
              autoComplete="off"
              onChange={(e) =>
                setNumbering((n) => ({
                  ...n,
                  suffix: e.target.value.toUpperCase().replace(/[^A-Z]/g, ""),
                }))
              }
            />
            <p className="es-num-hint">
              Suffix will be added after the number. Example:{" "}
              {numbering.suffix
                ? `${String(numbering.starting_number || 1).padStart(numbering.number_length || 4, "0")}${numbering.suffix}`
                : "0001A"}
            </p>
          </div>

          <div className="es-num-row-two">
            <div className="es-num-field">
              <label className="es-num-field-label" htmlFor="es-start">
                Starting Number <span className="es-required">*</span>
              </label>
              <input
                id="es-start"
                type="number"
                className="es-num-input"
                value={numbering.starting_number}
                min={1}
                max={999999999}
                onChange={(e) =>
                  setNumbering((n) => ({
                    ...n,
                    starting_number: Math.max(1, parseInt(e.target.value, 10) || 1),
                  }))
                }
              />
              <p className="es-num-hint">
                First number for this organisation (e.g. 1, 1001, 2001, 10001).
              </p>
            </div>
            <div className="es-num-field">
              <label className="es-num-field-label" htmlFor="es-length">
                Number Length <span className="es-required">*</span>
              </label>
              <input
                id="es-length"
                type="number"
                className="es-num-input"
                value={numbering.number_length}
                min={1}
                max={9}
                onChange={(e) =>
                  setNumbering((n) => ({
                    ...n,
                    number_length: Math.max(1, Math.min(9, parseInt(e.target.value, 10) || 4)),
                  }))
                }
              />
              <p className="es-num-hint">Total length of numeric part.</p>
            </div>
          </div>

          <div className="es-num-preview">
            <p className="es-num-preview-label">Preview</p>
            <p className="es-num-hint" style={{ marginBottom: 8 }}>
              Live preview only — nothing is saved until you click Save Numbering.
            </p>
            <div className="es-num-preview-codes">
              {previewCodes.map((code) => (
                <span key={code} className="es-num-preview-code">
                  {code}
                </span>
              ))}
              <span className="es-num-preview-ellipsis">…</span>
            </div>
          </div>

          <div className="es-next-code" aria-live="polite">
            <p className="es-num-preview-label">Next Employee Number</p>
            <p className="es-next-code-value">
              {nextCodeLoading ? "Loading…" : nextCode?.employee_code || "—"}
            </p>
            <p className="es-num-hint">
              Assigned from the saved configuration. Existing employee numbers are never renamed.
            </p>
          </div>

          <div className="es-num-save-row">
            {numDirty && (
              <span className="es-unsaved-hint">You have unsaved numbering changes</span>
            )}
            <button
              type="button"
              className="es-save-btn"
              onClick={handleNumSave}
              disabled={numSaving}
            >
              {numSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                  <polyline points="17 21 17 13 7 13 7 21"/>
                  <polyline points="7 3 7 8 15 8"/>
                </svg>
              )}
              {numSaving ? "Saving…" : "Save Numbering"}
            </button>
          </div>
        </div>

        <div className="es-panel-footer-info">
          <span className="es-info-icon">ℹ</span>
          Employee numbering applies only to this organisation. Changing the prefix does not rename existing employees.
        </div>
      </section>

      {drawerOpen && (
        <div
          className="fixed inset-0 z-[9000] flex justify-end bg-slate-900/40 backdrop-blur-[1px]"
          role="presentation"
          onClick={closeDrawer}
        >
          <aside
            className="h-full w-full max-w-md bg-white shadow-2xl border-l border-slate-200 flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="et-drawer-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <h2 id="et-drawer-title" className="text-lg font-bold text-slate-900">
                  {editing ? "Edit Employee Type" : "Add Employee Type"}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">Name and optional description</p>
              </div>
              <button
                type="button"
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                onClick={closeDrawer}
                aria-label="Close"
                disabled={etSaving}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form className="flex-1 overflow-y-auto px-5 py-5 space-y-4" onSubmit={handleEtSave}>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" htmlFor="et-name">
                  Type Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="et-name"
                  type="text"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Contract"
                />
                {formErrors.name && <p className="mt-1 text-xs text-red-600">{formErrors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" htmlFor="et-desc">
                  Description
                </label>
                <textarea
                  id="et-desc"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[96px] resize-y"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Optional notes for HR"
                />
                {formErrors.description && (
                  <p className="mt-1 text-xs text-red-600">{formErrors.description}</p>
                )}
              </div>
            </form>

            <div className="border-t border-slate-200 px-5 py-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                onClick={closeDrawer}
                disabled={etSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                onClick={handleEtSave}
                disabled={etSaving}
              >
                {etSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                {etSaving ? "Saving…" : "Save"}
              </button>
            </div>
          </aside>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={assignedToDelete > 0 ? "Deactivate employee type?" : "Delete employee type?"}
        message={
          deleteTarget
            ? assignedToDelete > 0
              ? `"${deleteTarget.name}" is assigned to ${assignedToDelete} employee${assignedToDelete === 1 ? "" : "s"}. It will be deactivated so existing employees are not changed.`
              : `Delete "${deleteTarget.name}"? Employees already using this type are not changed.`
            : ""
        }
        confirmLabel={assignedToDelete > 0 ? "Deactivate" : "Delete"}
        danger={assignedToDelete === 0}
        onConfirm={confirmDelete}
        onCancel={() => !etSaving && setDeleteTarget(null)}
        loading={etSaving}
      />

      <ConfirmDialog
        open={Boolean(leaveGuard)}
        title="Unsaved numbering changes"
        message={
          leaveGuard?.mode === "save"
            ? "Save employee numbering before continuing?"
            : "You have unsaved employee numbering changes. Leave without saving?"
        }
        confirmLabel={leaveGuard?.mode === "save" ? "Save and continue" : "Leave without saving"}
        danger={leaveGuard?.mode !== "save"}
        onConfirm={confirmLeave}
        onCancel={() => setLeaveGuard(null)}
        loading={numSaving}
      />
    </div>
  );
}

export function RedirectLegacyEmployeeSetup() {
  const { hash } = useLocation();
  const section = hashToSection(hash);
  const nextHash = section ? `#${section}` : "";
  return <Navigate to={`${EMP_CONFIG_PATH}${nextHash}`} replace />;
}
